// src/Pages/Product/Productdetails.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Col,
  Container,
  Row,
  Tab,
  Nav,
  ProgressBar,
  Form,
  Image,
  Card,
  Spinner,
  Alert,
  Accordion,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { FreeMode, Navigation } from "swiper/modules";
import type { SlideData } from "photoswipe";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import SimpleBar from "simplebar-react";

import "swiper/css";
import "swiper/css/navigation";
import "photoswipe/style.css";

import { productprogress } from "Common/data";
import { CommonService } from "Components/CommonService";
import { applyStorefrontSeo, restoreStorefrontSeoDefaults } from "Common/storeSeo";

import {
  useGetStoreProductBySlugQuery,
  useListStoreProductsQuery,
} from "../../redux/services/store/storeProductsApi";
import { useAddToCartMutation } from "../../redux/services/store/storeCartApi";
import { useListStoreFaqsQuery } from "../../redux/services/store/storeFaqApi";
import { saveBuyNowCheckoutDraft } from "../../Common/buyNowCheckout";

import avatar5 from "assets/images/users/avatar-5.jpg";

/** ---------------- Minimal types ---------------- */
type ProductImage = {
  id: number;
  image_group_id: string;
  variant_type: "thumb" | "medium" | "large";
  cdn_url: string;
  alt_text?: string | null;
  sort_order?: number;
  is_primary?: boolean;
};

type ProductInventory = {
  stock_qty?: string | number;
  reserved_qty?: string | number;
  allow_backorder?: boolean;
};

type VariantAttribute = {
  attribute_name: string;
  value_label?: string | null;
  value_bool?: boolean | null;
  value_id?: number | null;
  hex_color?: string | null;
};

type ProductVariant = {
  id: number;
  sku: string;
  barcode?: string | null;
  price: string | number;
  compare_at_price?: string | number | null;
  currency_code?: string | null;
  is_active?: boolean;
  attributes_json?: Record<string, unknown> | null;
  attributes?: VariantAttribute[];
  inventory?: ProductInventory | null;
  images?: ProductImage[];
};

type ProductOut = {
  id: number;
  name: string;
  slug: string;
  short_description?: string | null;
  description?: string | null;
  content?: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_keywords?: string | null;

  product_type: "simple" | "variable";
  status: "draft" | "active" | "inactive" | "archived";

  is_customizable?: boolean;
  allowed_colors?: string[];
  category_ids?: number[];

  reviews_count?: number;
  reviews_avg?: string | number;

  variants: ProductVariant[];
  images?: ProductImage[];
};

type SliderItem = {
  id: string;
  img: string;
  thumb: string;
  zoom?: string;
  alt?: string | null;
  isPrimary?: boolean;
};

type ImageDimensions = {
  width: number;
  height: number;
};

const DEFAULT_GALLERY_IMAGE_WIDTH = 1200;
const DEFAULT_GALLERY_IMAGE_HEIGHT = 1600;

const ATTRIBUTE_NAME_ALIASES = {
  style: ["style"],
  size: ["size"],
  color: ["color", "colour"],
} as const;

const matchesAttributeName = (attributeName: string | null | undefined, aliases: readonly string[]) =>
  aliases.includes(String(attributeName || "").trim().toLowerCase());

const formatFallbackAttributeLabel = (value: string): string => {
  const normalized = String(value || "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

  if (!normalized) return "";

  return normalized
    .split(" ")
    .filter(Boolean)
    .map((part) => {
      if (/^(\d+)?x{0,4}[sml]$/i.test(part)) {
        return part.toUpperCase();
      }
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ");
};

const stripHtmlToPlainText = (value: string | null | undefined): string =>
  String(value || "")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const truncateText = (value: string, maxLength: number): string => {
  const text = String(value || "").trim();
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 1)).trimEnd()}...`;
};

const copyTextToClipboard = async (value: string): Promise<void> => {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  if (typeof document === "undefined") {
    throw new Error("Clipboard is not available");
  }

  const textArea = document.createElement("textarea");
  textArea.value = value;
  textArea.setAttribute("readonly", "true");
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  textArea.style.pointerEvents = "none";
  document.body.appendChild(textArea);
  textArea.select();
  textArea.setSelectionRange(0, textArea.value.length);

  const copied = document.execCommand("copy");
  document.body.removeChild(textArea);

  if (!copied) {
    throw new Error("Copy command failed");
  }
};

const skeletonBaseStyle: React.CSSProperties = {
  width: "100%",
  background:
    "linear-gradient(90deg, rgba(240,240,240,0.95) 25%, rgba(248,248,248,1) 37%, rgba(240,240,240,0.95) 63%)",
  backgroundSize: "400% 100%",
  animation: "pdpSkeletonShimmer 1.4s ease infinite",
};

const ProductDetailsSkeleton = () => {
  return (
    <>
      <style>
        {`
          @keyframes pdpSkeletonShimmer {
            0% { background-position: 100% 0; }
            100% { background-position: 0 0; }
          }
        `}
      </style>

      <div style={{ minHeight: "70vh", background: "#fff" }}>
        <div style={{ height: 50 }} />

        <section className="section">
          <Container>
            <Row className="gx-3 gy-3">
              <Col lg={12}>
                <div
                  style={{
                    background: "#fff",
                    border: "1px solid rgba(0,0,0,.06)",
                    borderRadius: 16,
                    boxShadow: "0 8px 24px rgba(0,0,0,.06)",
                    padding: 16,
                  }}
                >
                  <Row className="gx-3 gy-3 align-items-start">
                    <Col lg={6}>
                      <div
                        style={{
                          borderRadius: 16,
                          overflow: "hidden",
                          background: "#f7f7f8",
                        }}
                      >
                        <div
                          style={{
                            ...skeletonBaseStyle,
                            height: 0,
                            paddingBottom: "133.33%",
                          }}
                        />
                      </div>

                      <Row className="g-2 mt-2">
                        {[1, 2, 3, 4, 5].map((item) => (
                          <Col xs={2} key={`pdp-thumb-skeleton-${item}`}>
                            <div
                              style={{
                                ...skeletonBaseStyle,
                                aspectRatio: "1 / 1",
                                borderRadius: 12,
                              }}
                            />
                          </Col>
                        ))}
                      </Row>
                    </Col>

                    <Col lg={6}>
                      <div>
                        <div
                          style={{
                            ...skeletonBaseStyle,
                            height: 14,
                            width: 120,
                            borderRadius: 4,
                            marginBottom: 14,
                          }}
                        />
                        <div
                          style={{
                            ...skeletonBaseStyle,
                            height: 30,
                            width: "76%",
                            borderRadius: 4,
                            marginBottom: 12,
                          }}
                        />
                        <div
                          style={{
                            ...skeletonBaseStyle,
                            height: 24,
                            width: 180,
                            borderRadius: 4,
                            marginBottom: 20,
                          }}
                        />

                        <div
                          style={{
                            ...skeletonBaseStyle,
                            height: 16,
                            width: 150,
                            borderRadius: 4,
                            marginBottom: 10,
                          }}
                        />
                        <div
                          style={{
                            ...skeletonBaseStyle,
                            height: 16,
                            width: "100%",
                            borderRadius: 4,
                            marginBottom: 10,
                          }}
                        />
                        <div
                          style={{
                            ...skeletonBaseStyle,
                            height: 16,
                            width: "88%",
                            borderRadius: 4,
                            marginBottom: 20,
                          }}
                        />

                        <div
                          style={{
                            borderTop: "1px solid rgba(0,0,0,.06)",
                            margin: "16px 0",
                          }}
                        />

                        <div
                          style={{
                            ...skeletonBaseStyle,
                            height: 16,
                            width: 100,
                            borderRadius: 4,
                            marginBottom: 12,
                          }}
                        />

                        <div className="d-flex gap-2 mb-3">
                          <div
                            style={{
                              ...skeletonBaseStyle,
                              height: 38,
                              width: 120,
                              borderRadius: 999,
                            }}
                          />
                        </div>

                        <div
                          style={{
                            ...skeletonBaseStyle,
                            height: 16,
                            width: 90,
                            borderRadius: 4,
                            marginBottom: 12,
                          }}
                        />
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          {[1, 2, 3].map((item) => (
                            <div
                              key={`pdp-style-skeleton-${item}`}
                              style={{
                                ...skeletonBaseStyle,
                                height: 34,
                                width: 78,
                                borderRadius: 999,
                              }}
                            />
                          ))}
                        </div>

                        <div
                          style={{
                            ...skeletonBaseStyle,
                            height: 16,
                            width: 80,
                            borderRadius: 4,
                            marginBottom: 12,
                          }}
                        />
                        <div className="d-flex flex-wrap gap-2 mb-4">
                          {[1, 2, 3, 4].map((item) => (
                            <div
                              key={`pdp-size-skeleton-${item}`}
                              style={{
                                ...skeletonBaseStyle,
                                width: 34,
                                height: 34,
                                borderRadius: 999,
                              }}
                            />
                          ))}
                        </div>

                        <div className="d-flex gap-2">
                          <div
                            style={{
                              ...skeletonBaseStyle,
                              height: 44,
                              flex: 1,
                              borderRadius: 10,
                            }}
                          />
                          <div
                            style={{
                              ...skeletonBaseStyle,
                              height: 44,
                              flex: 1,
                              borderRadius: 10,
                            }}
                          />
                          <div
                            style={{
                              ...skeletonBaseStyle,
                              height: 44,
                              width: 52,
                              borderRadius: 10,
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="section pt-0">
          <Container>
            <div
              style={{
                background: "#fff",
                border: "1px solid rgba(0,0,0,.06)",
                borderRadius: 16,
                boxShadow: "0 8px 24px rgba(0,0,0,.06)",
                padding: 16,
              }}
            >
              <div className="d-flex gap-3 mb-4">
                <div
                  style={{
                    ...skeletonBaseStyle,
                    height: 38,
                    width: 140,
                    borderRadius: 8,
                  }}
                />
                <div
                  style={{
                    ...skeletonBaseStyle,
                    height: 38,
                    width: 150,
                    borderRadius: 8,
                  }}
                />
              </div>

              <div
                style={{
                  ...skeletonBaseStyle,
                  height: 16,
                  width: "100%",
                  borderRadius: 4,
                  marginBottom: 10,
                }}
              />
              <div
                style={{
                  ...skeletonBaseStyle,
                  height: 16,
                  width: "95%",
                  borderRadius: 4,
                  marginBottom: 10,
                }}
              />
              <div
                style={{
                  ...skeletonBaseStyle,
                  height: 16,
                  width: "88%",
                  borderRadius: 4,
                  marginBottom: 10,
                }}
              />
              <div
                style={{
                  ...skeletonBaseStyle,
                  height: 16,
                  width: "82%",
                  borderRadius: 4,
                }}
              />
            </div>
          </Container>
        </section>

        <section className="section pt-0">
          <Container>
            <Row className="align-items-center mb-4">
              <Col lg={12}>
                <div className="d-flex align-items-center justify-content-between">
                  <div
                    style={{
                      ...skeletonBaseStyle,
                      height: 28,
                      width: 180,
                      borderRadius: 4,
                    }}
                  />
                </div>
              </Col>
            </Row>

            <Row className="gy-4">
              {[1, 2, 3, 4].map((item) => (
                <Col xl={3} lg={4} md={6} sm={6} key={`similar-skeleton-${item}`}>
                  <Card
                    className="border-0 shadow-none h-100"
                    style={{
                      borderRadius: 18,
                      overflow: "hidden",
                      boxShadow: "0 8px 24px rgba(15,23,42,.05)",
                    }}
                  >
                    <div
                      style={{
                        ...skeletonBaseStyle,
                        height: 0,
                        paddingBottom: "133.33%",
                      }}
                    />
                    <Card.Body>
                      <div
                        style={{
                          ...skeletonBaseStyle,
                          height: 18,
                          width: "92%",
                          borderRadius: 4,
                          marginBottom: 8,
                        }}
                      />
                      <div
                        style={{
                          ...skeletonBaseStyle,
                          height: 18,
                          width: "68%",
                          borderRadius: 4,
                          marginBottom: 14,
                        }}
                      />
                      <div
                        style={{
                          ...skeletonBaseStyle,
                          height: 18,
                          width: "44%",
                          borderRadius: 4,
                          marginBottom: 16,
                        }}
                      />
                      <div
                        style={{
                          ...skeletonBaseStyle,
                          height: 40,
                          width: 110,
                          borderRadius: 999,
                        }}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        <CommonService />
      </div>
    </>
  );
};

const Productdetails = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [slug]);

  const { data, isLoading, isFetching, isError, error } = useGetStoreProductBySlugQuery(
    { slug: slug || "" },
    { skip: !slug }
  );

  const product = (data as unknown as ProductOut) || null;
  const primaryCategoryId = product?.category_ids?.[0] ?? null;
  const faqLangCode = useMemo(() => {
    const raw = (i18n.resolvedLanguage || i18n.language || "en").toString().trim();
    return raw || "en";
  }, [i18n.language, i18n.resolvedLanguage]);

  const { data: similarData, isFetching: isSimilarFetching } = useListStoreProductsQuery(
    {
      category_id: primaryCategoryId,
      skip: 0,
      limit: 8,
      sort: "popular",
      include_total: false,
      include_filters: false,
    },
    {
      skip: !primaryCategoryId,
    }
  );

  const [count, setCount] = useState(1);
  const safeCount = count > 0 ? count : 1;

  const [sliderImg, setSliderImg] = useState<SliderItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [activeDetailTab, setActiveDetailTab] = useState("Description");

  const [customizationEnabled, setCustomizationEnabled] = useState(false);
  const [customReqColor, setCustomReqColor] = useState("");
  const [customNote, setCustomNote] = useState("");

  const [wishlisted, setWishlisted] = useState(false);
  const [shareFeedback, setShareFeedback] = useState<{
    variant: "success" | "danger";
    message: string;
  } | null>(null);

  const mainSwiperRef = useRef<SwiperType | null>(null);
  const gallerySectionRef = useRef<HTMLDivElement | null>(null);
  const photoSwipeRef = useRef<PhotoSwipeLightbox | null>(null);
  const galleryTriggerRef = useRef<HTMLElement | null>(null);
  const galleryItemRefs = useRef(new Map<string, HTMLDivElement>());
  const previousVariantIdRef = useRef<number | null>(null);
  const colorSelectionPendingRef = useRef(false);

  const [imageDimensions, setImageDimensions] = useState<Record<string, ImageDimensions>>({});
  const shouldLoadFaq = activeDetailTab === "FAQ";

  const { data: faqData, isLoading: isFaqLoading, isFetching: isFaqFetching } = useListStoreFaqsQuery(
    {
      lang_code: faqLangCode,
      fallback_lang_code: "en",
      active_only: true,
      limit: 12,
    },
    {
      skip: !shouldLoadFaq,
    }
  );

  const [addToCart, { isLoading: isAdding, error: addErr }] = useAddToCartMutation();

  useEffect(() => {
    setCustomizationEnabled(false);
    setCustomReqColor("");
    setCustomNote("");
    setWishlisted(false);
    setShareFeedback(null);
  }, [product?.id]);

  useEffect(() => {
    if (!shareFeedback) return undefined;

    const timeoutId = window.setTimeout(() => {
      setShareFeedback(null);
    }, 2600);

    return () => window.clearTimeout(timeoutId);
  }, [shareFeedback]);

  const toNum = (v: unknown): number => {
    if (v == null) return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const storeImageDimensions = (id: string, width: number, height: number) => {
    const safeWidth = Math.max(1, Math.round(width || 0));
    const safeHeight = Math.max(1, Math.round(height || 0));
    if (!safeWidth || !safeHeight) return;

    setImageDimensions((prev) => {
      const current = prev[id];
      if (current?.width === safeWidth && current?.height === safeHeight) {
        return prev;
      }

      return {
        ...prev,
        [id]: { width: safeWidth, height: safeHeight },
      };
    });
  };

  const normStyle = (a: any) => String(a?.Style ?? a?.style ?? a?.STYLE ?? "").trim();
  const normSize = (a: any) => String(a?.Size ?? a?.size ?? a?.SIZE ?? "").trim();
  const normColor = (a: any) =>
    String(a?.Color ?? a?.color ?? a?.Colour ?? a?.colour ?? a?.COLOUR ?? "").trim();

  const findVariantAttribute = (
    variant: ProductVariant | null | undefined,
    aliases: readonly string[]
  ): VariantAttribute | undefined =>
    (variant?.attributes || []).find((item) => matchesAttributeName(item?.attribute_name, aliases));

  const buildValueLabelMap = (
    variants: ProductVariant[],
    getRawValue: (attrs: Record<string, unknown> | null | undefined) => string,
    aliases: readonly string[]
  ) => {
    const map = new Map<string, string>();

    for (const variant of variants) {
      const rawValue = getRawValue(variant?.attributes_json || {});
      if (!rawValue || map.has(rawValue)) continue;

      const attr = findVariantAttribute(variant, aliases);
      const valueLabel = String(attr?.value_label || "").trim();

      map.set(rawValue, valueLabel || formatFallbackAttributeLabel(rawValue));
    }

    return map;
  };

  const activeVariants = useMemo(() => {
    if (!product) return [] as ProductVariant[];
    return (product.variants || []).filter((v) => (v?.is_active ?? true) !== false);
  }, [product]);

  const buildSlides = (imgs: ProductImage[], fallbackAlt: string | null): SliderItem[] => {
    const groups = new Map<string, ProductImage[]>();

    (imgs || []).forEach((im: ProductImage) => {
      if (!im?.cdn_url) return;
      const key = im.image_group_id ? String(im.image_group_id) : `img-${im.id}`;
      const arr = groups.get(key) || [];
      arr.push(im);
      groups.set(key, arr);
    });

    const rankMain = (t?: ProductImage["variant_type"]) =>
      t === "medium" ? 3 : t === "large" ? 2 : t === "thumb" ? 1 : 0;
    const rankThumb = (t?: ProductImage["variant_type"]) =>
      t === "thumb" ? 3 : t === "medium" ? 2 : t === "large" ? 1 : 0;
    const rankZoom = (t?: ProductImage["variant_type"]) =>
      t === "large" ? 3 : t === "medium" ? 2 : t === "thumb" ? 1 : 0;

    const out: Array<SliderItem & { _sort?: number }> = [];

    Array.from(groups).forEach(([key, arr]) => {
      const mainPick = [...arr].sort((a, b) => rankMain(b.variant_type) - rankMain(a.variant_type))[0];
      const thumbPick = [...arr].sort((a, b) => rankThumb(b.variant_type) - rankThumb(a.variant_type))[0];
      const zoomPick = [...arr].sort((a, b) => rankZoom(b.variant_type) - rankZoom(a.variant_type))[0];

      const mainUrl = (mainPick && mainPick.cdn_url) || (thumbPick && thumbPick.cdn_url) || "";
      const thumbUrl = (thumbPick && thumbPick.cdn_url) || (mainPick && mainPick.cdn_url) || "";
      const zoomUrl =
        (zoomPick && zoomPick.cdn_url) ||
        (mainPick && mainPick.cdn_url) ||
        (thumbPick && thumbPick.cdn_url) ||
        "";
      if (!mainUrl || !thumbUrl) return;

      const isPrimary = arr.some((x) => Boolean(x.is_primary));
      const sort = arr.reduce((m: number, x: ProductImage) => {
        const so = typeof x.sort_order === "number" ? x.sort_order : m;
        return Math.min(m, so);
      }, 999999);

      const alt = (arr.find((x) => !!x.alt_text)?.alt_text ?? fallbackAlt) as string | null;

      out.push({ id: key, img: mainUrl, thumb: thumbUrl, zoom: zoomUrl || mainUrl, alt, isPrimary, _sort: sort });
    });

    out.sort((a, b) => {
      const ap = a.isPrimary ? 0 : 1;
      const bp = b.isPrimary ? 0 : 1;
      if (ap !== bp) return ap - bp;
      if ((a._sort ?? 0) !== (b._sort ?? 0)) return (a._sort ?? 0) - (b._sort ?? 0);
      return a.id.localeCompare(b.id);
    });

    return out;
  };

  const styleValues = useMemo(() => {
    const vals = new Set<string>();
    for (const v of activeVariants) {
      const st = normStyle(v?.attributes_json || {});
      if (st) vals.add(st);
    }
    return Array.from(vals);
  }, [activeVariants]);

  const styleLabelMap = useMemo(
    () => buildValueLabelMap(activeVariants, normStyle, ATTRIBUTE_NAME_ALIASES.style),
    [activeVariants]
  );

  useEffect(() => {
    if (!product) return;
    if (styleValues.length > 0 && (!selectedStyle || !styleValues.includes(selectedStyle))) {
      setSelectedStyle(styleValues[0]);
      return;
    }
    if (styleValues.length === 0 && selectedStyle !== null) {
      setSelectedStyle(null);
    }
  }, [product, styleValues, selectedStyle]);

  const variantsForStyle = useMemo(() => {
    if (!selectedStyle) return activeVariants;
    return activeVariants.filter((v) => normStyle(v?.attributes_json || {}) === selectedStyle);
  }, [activeVariants, selectedStyle]);

  const sizeValues = useMemo(() => {
    const vals = new Set<string>();
    for (const v of variantsForStyle) {
      const s = normSize(v?.attributes_json || {});
      if (s) vals.add(s);
    }
    return Array.from(vals);
  }, [variantsForStyle]);

  const sizeLabelMap = useMemo(
    () => buildValueLabelMap(variantsForStyle, normSize, ATTRIBUTE_NAME_ALIASES.size),
    [variantsForStyle]
  );

  const colorValues = useMemo(() => {
    const vals = new Set<string>();
    for (const v of variantsForStyle) {
      const c = normColor(v?.attributes_json || {});
      if (c) vals.add(c);
    }
    return Array.from(vals);
  }, [variantsForStyle]);

  const colorLabelMap = useMemo(
    () => buildValueLabelMap(variantsForStyle, normColor, ATTRIBUTE_NAME_ALIASES.color),
    [variantsForStyle]
  );

  const getColorHex = (v: ProductVariant | null | undefined): string | null => {
    const a = findVariantAttribute(v, ATTRIBUTE_NAME_ALIASES.color);
    const hex = (a?.hex_color || "").trim();
    return hex ? hex : null;
  };

  const colorHexMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const v of variantsForStyle) {
      const label = normColor(v?.attributes_json || {});
      if (!label) continue;
      const hex = getColorHex(v);
      if (hex && !m.has(label)) m.set(label, hex);
    }
    return m;
  }, [variantsForStyle]);

  useEffect(() => {
    if (!product) return;

    if (sizeValues.length > 0) {
      if (!selectedSize || !sizeValues.includes(selectedSize)) {
        setSelectedSize(sizeValues[0]);
      }
    } else if (selectedSize !== null) {
      setSelectedSize(null);
    }

    if (colorValues.length > 0) {
      if (!selectedColor || !colorValues.includes(selectedColor)) {
        setSelectedColor(colorValues[0]);
      }
    } else if (selectedColor !== null) {
      setSelectedColor(null);
    }
  }, [product, selectedStyle, sizeValues, colorValues, selectedSize, selectedColor]);

  const selectedVariant = useMemo(() => {
    if (!product) return null;

    const pool = variantsForStyle.length ? variantsForStyle : activeVariants;

    const found = pool.find((v) => {
      const a = (v.attributes_json || {}) as any;

      const style = normStyle(a);
      const size = normSize(a);
      const color = normColor(a);

      const okStyle = selectedStyle ? style === selectedStyle : true;
      const okSize = sizeValues.length > 0 && selectedSize ? size === selectedSize : true;
      const okColor = colorValues.length > 0 && selectedColor ? color === selectedColor : true;

      return okStyle && okSize && okColor;
    });

    return found || pool[0] || activeVariants[0] || null;
  }, [
    product,
    activeVariants,
    variantsForStyle,
    selectedStyle,
    selectedSize,
    selectedColor,
    sizeValues.length,
    colorValues.length,
  ]);

  const sliderProduct: SliderItem[] = useMemo(() => {
    if (!product) return [];

    const variantImgs = Array.isArray(selectedVariant?.images) ? (selectedVariant!.images as ProductImage[]) : [];
    const productImgs = Array.isArray(product.images) ? (product.images as ProductImage[]) : [];

    const source = variantImgs.length > 0 ? variantImgs : productImgs;
    return buildSlides(source, product.name);
  }, [product, selectedVariant]);

  const photoSwipeSlides = useMemo<SlideData[]>(
    () =>
      sliderImg.map((item) => {
        const dims = imageDimensions[item.id];
        return {
          src: item.zoom || item.img,
          msrc: item.thumb,
          alt: item.alt || product?.name || "Product image",
          width: dims?.width || DEFAULT_GALLERY_IMAGE_WIDTH,
          height: dims?.height || DEFAULT_GALLERY_IMAGE_HEIGHT,
        };
      }),
    [sliderImg, imageDimensions, product?.name]
  );

  const similarProducts = useMemo(() => {
    const items = similarData?.items || [];
    if (!product) return [];

    return items
      .filter((it) => it.id !== product.id && it.slug !== product.slug)
      .slice(0, 4)
      .map((it) => {
        const currency = it.currency_code || "INR";
        const price = it.price_from ? `${currency} ${it.price_from}` : "";
        const compareAt = it.compare_at_from ? `${currency} ${it.compare_at_from}` : null;

        return {
          id: it.id,
          slug: it.slug,
          title: it.name,
          img: it.image?.cdn_url || "",
          alt: it.image?.alt_text || it.name,
          price,
          compareAt,
        };
      });
  }, [similarData, product]);
  const faqItems = faqData?.items ?? [];

  useEffect(() => {
    const currentVariantId = selectedVariant?.id ?? null;
    const variantChanged = previousVariantIdRef.current !== currentVariantId;

    if (sliderProduct.length > 0) {
      setSliderImg(sliderProduct);

      const stillExists =
        !variantChanged && selectedId && sliderProduct.some((x) => x.id === selectedId);
      const nextId = stillExists ? selectedId : sliderProduct[0].id;
      const nextIndex = Math.max(
        0,
        sliderProduct.findIndex((x) => x.id === nextId)
      );

      setSelectedId(nextId);
      mainSwiperRef.current?.slideTo(nextIndex, 0);

      if (
        colorSelectionPendingRef.current &&
        typeof window !== "undefined" &&
        window.innerWidth < 992
      ) {
        gallerySectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    } else {
      setSelectedId(null);
      setSliderImg([]);
      mainSwiperRef.current?.slideTo(0, 0);
    }

    previousVariantIdRef.current = currentVariantId;
    colorSelectionPendingRef.current = false;
  }, [sliderProduct, selectedId, selectedVariant?.id]);

  const htmlFullDesc = useMemo(() => {
    return (product?.description || product?.short_description || product?.content || "").toString();
  }, [product]);

  const pricing = useMemo(() => {
    const v = selectedVariant;
    const currency = (v?.currency_code as string | undefined) || "INR";

    const pNum = v ? toNum(v.price) : 0;
    const showPrice = pNum.toFixed(2);

    const cNum = v?.compare_at_price != null ? toNum(v.compare_at_price) : 0;
    const compareAt = cNum > pNum && cNum > 0 ? cNum : null;

    const discountPct =
      compareAt != null && compareAt > 0 && pNum > 0 ? Math.round(((compareAt - pNum) / compareAt) * 100) : null;

    return { currency, showPrice, compareAt, discountPct };
  }, [selectedVariant]);

  const errorText = useMemo(() => {
    if (!isError) return null;
    const anyErr = error as any;
    return anyErr?.data?.detail || anyErr?.error || "Failed to load product";
  }, [isError, error]);

  const addErrorText = useMemo(() => {
    const e: any = addErr;
    return e?.data?.detail || e?.error || e?.message || null;
  }, [addErr]);

  const buyNowVariantSummary = useMemo(() => {
    const parts: string[] = [];

    if (selectedStyle) {
      parts.push(`Style: ${styleLabelMap.get(selectedStyle) || formatFallbackAttributeLabel(selectedStyle)}`);
    }

    if (selectedSize) {
      parts.push(`Size: ${sizeLabelMap.get(selectedSize) || formatFallbackAttributeLabel(selectedSize)}`);
    }

    if (selectedColor) {
      parts.push(`Color: ${colorLabelMap.get(selectedColor) || formatFallbackAttributeLabel(selectedColor)}`);
    }

    return parts.join(", ") || selectedVariant?.sku || null;
  }, [
    colorLabelMap,
    selectedColor,
    selectedSize,
    selectedStyle,
    selectedVariant?.sku,
    sizeLabelMap,
    styleLabelMap,
  ]);

  const shareUrl = useMemo(() => {
    if (!product?.slug) return "";

    if (typeof window !== "undefined" && window.location?.origin) {
      return `${window.location.origin}/product/${product.slug}`;
    }

    return `https://fionaziya.in/product/${product.slug}`;
  }, [product?.slug]);

  const shareText = useMemo(() => {
    if (!product) return "";

    const baseTitle = buyNowVariantSummary ? `${product.name} - ${buyNowVariantSummary}` : product.name;
    const description = stripHtmlToPlainText(
      product.seo_description ||
        product.short_description ||
        product.description ||
        product.content ||
        ""
    );

    if (!description) return baseTitle;

    return truncateText(`${baseTitle}. ${description}`, 180);
  }, [buyNowVariantSummary, product]);

  const seoTitle = useMemo(() => {
    if (!product) return "FionaZiya";

    const rawSeoTitle = String(product.seo_title || "").trim();
    return rawSeoTitle || `${product.name} | FionaZiya`;
  }, [product]);

  const seoDescription = useMemo(() => {
    const raw = stripHtmlToPlainText(
      product?.seo_description ||
        product?.short_description ||
        product?.description ||
        product?.content ||
        ""
    );

    return truncateText(raw || "Discover handcrafted and occasion-ready styles from FionaZiya.", 220);
  }, [product]);

  const seoKeywords = useMemo(
    () => String(product?.seo_keywords || "").trim(),
    [product?.seo_keywords]
  );

  const seoImageUrl = useMemo(() => {
    return (
      sliderProduct[0]?.img ||
      selectedVariant?.images?.[0]?.cdn_url ||
      product?.images?.[0]?.cdn_url ||
      ""
    );
  }, [sliderProduct, selectedVariant?.images, product?.images]);

  const seoImageAlt = useMemo(() => {
    return (
      sliderProduct[0]?.alt ||
      selectedVariant?.images?.[0]?.alt_text ||
      product?.images?.[0]?.alt_text ||
      product?.name ||
      "FionaZiya"
    );
  }, [sliderProduct, selectedVariant?.images, product?.images, product?.name]);

  const seoJsonLd = useMemo(() => {
    if (!product || !shareUrl) return null;

    const amount =
      selectedVariant?.price != null ? toNum(selectedVariant.price) : null;
    const priceCurrency =
      String(selectedVariant?.currency_code || "INR").trim().toUpperCase() || "INR";

    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description: seoDescription,
      image: seoImageUrl ? [seoImageUrl] : undefined,
      url: shareUrl,
      sku: selectedVariant?.sku || undefined,
      brand: {
        "@type": "Brand",
        name: "FionaZiya",
      },
      offers:
        amount !== null
          ? {
              "@type": "Offer",
              price: amount.toFixed(2),
              priceCurrency,
              availability:
                product.status === "active"
                  ? "https://schema.org/InStock"
                  : "https://schema.org/OutOfStock",
              url: shareUrl,
            }
          : undefined,
    };
  }, [
    product,
    seoDescription,
    seoImageUrl,
    selectedVariant?.currency_code,
    selectedVariant?.price,
    selectedVariant?.sku,
    shareUrl,
  ]);

  useEffect(() => {
    if (!product || !shareUrl) return;

    applyStorefrontSeo({
      title: seoTitle,
      description: seoDescription,
      keywords: seoKeywords || null,
      canonicalUrl: shareUrl,
      imageUrl: seoImageUrl || null,
      imageAlt: seoImageAlt,
      jsonLd: seoJsonLd,
    });

    return () => {
      restoreStorefrontSeoDefaults();
    };
  }, [
    product?.id,
    seoDescription,
    seoImageAlt,
    seoImageUrl,
    seoJsonLd,
    seoKeywords,
    seoTitle,
    shareUrl,
  ]);

  const canBuy = !!selectedVariant?.id;

  const dispatchNote =
    "Each piece is thoughtfully prepared to order and will be dispatched within 15–20 business days.";
  const transitNote =
    "Once dispatched, delivery time will vary based on your selected shipping method and destination.";

  const handleAddToCart = async () => {
    if (!selectedVariant?.id) return;

    await addToCart({
      variant_id: selectedVariant.id,
      qty: safeCount,
      customization_enabled: customizationEnabled,
      customization_color: customizationEnabled ? customReqColor.trim() || null : null,
      customization_note: customizationEnabled ? customNote.trim() || null : null,
    }).unwrap();
  };

  const handleBuyNow = () => {
    if (!product || !selectedVariant?.id) return;

    const unitPrice = toNum(selectedVariant.price);
    const compareAtPrice = toNum(selectedVariant.compare_at_price);
    const preferredImage =
      sliderProduct[0]?.img ||
      selectedVariant.images?.[0]?.cdn_url ||
      product.images?.[0]?.cdn_url ||
      null;
    const preferredAlt =
      sliderProduct[0]?.alt ||
      selectedVariant.images?.[0]?.alt_text ||
      product.images?.[0]?.alt_text ||
      product.name;

    saveBuyNowCheckoutDraft({
      variant_id: selectedVariant.id,
      qty: safeCount,
      product_id: product.id,
      product_name: product.name,
      product_slug: product.slug,
      image_url: preferredImage,
      image_alt: preferredAlt,
      unit_price: unitPrice,
      compare_at_price: compareAtPrice > unitPrice ? compareAtPrice : null,
      currency_code: (selectedVariant.currency_code || "INR").toUpperCase(),
      variant_name: buyNowVariantSummary,
      customization_enabled: customizationEnabled,
      customization_color: customizationEnabled ? customReqColor.trim() || null : null,
      customization_note: customizationEnabled ? customNote.trim() || null : null,
    });

    navigate("/shop/checkout?mode=buy-now");
  };

  const handleShareProduct = async () => {
    if (!product || !shareUrl) return;

    const sharePayload = {
      title: product.name,
      text: shareText,
      url: shareUrl,
    };

    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share(sharePayload);
        setShareFeedback(null);
        return;
      } catch (error) {
        if ((error as Error)?.name === "AbortError") {
          return;
        }
      }
    }

    try {
      await copyTextToClipboard(shareUrl);
      setShareFeedback({
        variant: "success",
        message: "Product link copied. You can share it anywhere.",
      });
    } catch {
      setShareFeedback({
        variant: "danger",
        message: "Could not open share options. Please copy the link from your browser.",
      });
    }
  };

  useEffect(() => {
    if (photoSwipeSlides.length === 0) {
      photoSwipeRef.current?.destroy();
      photoSwipeRef.current = null;
      return;
    }

    const lightbox = new PhotoSwipeLightbox({
      dataSource: photoSwipeSlides,
      pswpModule: () => import("photoswipe"),
      bgOpacity: 0.96,
      spacing: 0.08,
      loop: photoSwipeSlides.length > 2,
      wheelToZoom: true,
      pinchToClose: true,
      closeOnVerticalDrag: true,
      mouseMovePan: true,
      showHideAnimationType: "zoom",
      imageClickAction: "zoom-or-close",
      tapAction: "toggle-controls",
      doubleTapAction: "zoom",
      arrowPrev: photoSwipeSlides.length > 1,
      arrowNext: photoSwipeSlides.length > 1,
      counter: photoSwipeSlides.length > 1,
      zoom: true,
      close: true,
      padding: {
        top: 24,
        bottom: 24,
        left: 24,
        right: 24,
      },
      initialZoomLevel: "fit",
      secondaryZoomLevel: 2.2,
      maxZoomLevel: 4,
    });

    lightbox.addFilter("thumbEl", (thumbnail, _itemData, index) => {
      const slideItem = sliderImg[index];
      return (
        galleryTriggerRef.current ||
        (slideItem ? galleryItemRefs.current.get(slideItem.id) : null) ||
        thumbnail ||
        document.body
      );
    });

    lightbox.on("change", () => {
      const currentIndex = lightbox.pswp?.currIndex ?? 0;
      const currentItem = sliderImg[currentIndex];
      if (!currentItem?.id) return;

      setSelectedId(currentItem.id);
      mainSwiperRef.current?.slideTo(currentIndex);
      galleryTriggerRef.current = galleryItemRefs.current.get(currentItem.id) || galleryTriggerRef.current;
    });

    lightbox.on("close", () => {
      galleryTriggerRef.current = null;
    });

    lightbox.init();
    photoSwipeRef.current = lightbox;

    return () => {
      if (photoSwipeRef.current === lightbox) {
        photoSwipeRef.current = null;
      }
      lightbox.destroy();
    };
  }, [photoSwipeSlides, sliderImg]);

  const showCustomization = Boolean(product?.is_customizable);

  const openGalleryAt = (index: number, sourceEl?: HTMLElement | null) => {
    const safeIndex = Math.max(0, Math.min(index, Math.max(sliderImg.length - 1, 0)));
    const currentItem = sliderImg[safeIndex];

    if (currentItem?.id) {
      setSelectedId(currentItem.id);
      mainSwiperRef.current?.slideTo(safeIndex);
    }

    galleryTriggerRef.current =
      sourceEl || (currentItem ? galleryItemRefs.current.get(currentItem.id) || null : null);

    photoSwipeRef.current?.loadAndOpen(safeIndex);
  };

  if (!slug) {
    return (
      <section className="section">
        <Container>
          <Alert variant="warning" className="mb-0">
            Product slug is missing in URL.
          </Alert>
        </Container>
      </section>
    );
  }

  if (isLoading && !data) {
    return <ProductDetailsSkeleton />;
  }

  return (
    <React.Fragment>
      <div style={{ height: 50 }} />

      <style>
        {`
          .pdp-card{
            background:#fff;
            border:1px solid rgba(0,0,0,.06);
            border-radius:16px;
            box-shadow:0 8px 24px rgba(0,0,0,.06);
          }
          .pdp-card-pad{ padding:16px; }
          .pdp-soft-bg{
            background:linear-gradient(180deg, rgba(0,0,0,.02), rgba(0,0,0,0));
            border-radius:16px;
          }

          .pdp-main-img{
            border-radius:14px;
            background:#f7f7f8;
            overflow:hidden;
          }
          .pdp-badge{
            position:absolute; top:14px; left:14px; z-index:5;
            padding:6px 10px; border-radius:999px; font-size:12px;
            background:rgba(220,53,69,.95); color:#fff;
            box-shadow:0 6px 18px rgba(220,53,69,.25);
          }

          .pdp-main-slide-click{
            cursor:zoom-in;
          }

          .pdp-gallery-open{
            position:absolute;
            right:14px;
            bottom:14px;
            z-index:10;
            border:0;
            background:rgba(16,18,24,.78);
            color:#fff;
            padding:10px 14px;
            border-radius:999px;
            font-size:12px;
            font-weight:600;
            backdrop-filter:blur(10px);
            box-shadow:0 12px 30px rgba(0,0,0,.18);
            transition:all .2s ease;
          }
          .pdp-gallery-open:hover{
            background:rgba(16,18,24,.9);
            transform:translateY(-1px);
          }

          .pdp-thumbs{ margin-top:10px; }
          .pdp-thumb{
            border-radius:14px; overflow:hidden;
            border:1px solid rgba(0,0,0,.08);
            background:#fff;
            box-shadow:0 6px 14px rgba(0,0,0,.06);
            transition:transform .15s ease, box-shadow .15s ease, border-color .15s ease;
            cursor:pointer;
          }
          .pdp-thumb:hover{ transform:translateY(-1px); box-shadow:0 10px 18px rgba(0,0,0,.08); }
          .pdp-thumb.is-active{ border-color:#111827; box-shadow:0 10px 20px rgba(17,24,39,.12); }
          .pdp-thumb img{ display:block; width:100%; height:100%; object-fit:cover; }

          .pdp-title{ font-size:22px; font-weight:600; }
          .pdp-price{ font-size:24px; font-weight:700; }

          .pdp-similar-card{
            border: 1px solid rgba(0,0,0,.06) !important;
            border-radius: 18px !important;
            overflow: hidden;
            background:#fff;
            box-shadow:0 8px 24px rgba(15,23,42,.05);
            transition:transform .25s ease, box-shadow .25s ease;
          }
          .pdp-similar-card:hover{
            transform:translateY(-4px);
            box-shadow:0 16px 36px rgba(15,23,42,.12);
          }
          .pdp-similar-image-wrap{
            background:#f8f9fa;
            overflow:hidden;
          }
          .pdp-similar-image-box{
            width:100%;
            aspect-ratio:3 / 4;
            overflow:hidden;
          }
          .pdp-similar-image{
            width:100%;
            height:100%;
            object-fit:cover;
            object-position:top center;
            display:block;
            transition:transform .4s ease;
          }
          .pdp-similar-card:hover .pdp-similar-image{
            transform:scale(1.05);
          }
          .pdp-similar-title{
            color:#111827;
            text-decoration:none;
            font-size:16px;
            font-weight:600;
            line-height:1.35;
            min-height:43px;
            display:-webkit-box;
            -webkit-line-clamp:2;
            -webkit-box-orient:vertical;
            overflow:hidden;
          }
          .pdp-similar-title:hover{
            color:#111827;
          }
          .pdp-similar-price{
            font-size:18px;
            font-weight:700;
            color:#111;
            line-height:1;
          }
          .pdp-similar-old-price{
            font-size:14px;
            font-weight:500;
            color:#9ca3af;
            text-decoration:line-through;
            line-height:1;
          }

          .pdp-color-option{
            cursor:pointer !important;
            transition:transform .15s ease, box-shadow .15s ease, border-color .15s ease;
            box-shadow:0 6px 14px rgba(0,0,0,.06);
            border:1px solid rgba(0,0,0,.12);
          }
          .pdp-color-option:hover{
            transform:translateY(-1px);
            box-shadow:0 10px 18px rgba(0,0,0,.10);
          }
          .pdp-color-option.is-active{
            box-shadow:0 0 0 3px rgba(17,24,39,.16), 0 10px 18px rgba(0,0,0,.10);
            border-color:#111827 !important;
          }
          .clothe-colors li{
            cursor:pointer;
          }
          .clothe-colors li label{
            cursor:pointer !important;
          }

          @media (min-width: 992px){
            .pdp-details{
              border-left:1px solid rgba(0,0,0,.06);
              padding-left:18px;
            }
          }
          @media (max-width: 991.98px){
            .pdp-details{
              border-top:1px solid rgba(0,0,0,.06);
              padding-top:14px;
              margin-top:8px;
            }
            .pdp-card-pad{ padding:12px; }
          }

          @media (max-width: 991.98px){
            .mobile-cartbar-spacer{ height: 76px; }
            .mobile-cartbar{
              position:fixed; left:0; right:0; bottom:0;
              z-index:1030;
              background:#fff;
              border-top:1px solid rgba(0,0,0,.08);
              box-shadow:0 -6px 20px rgba(0,0,0,.06);
              padding:10px 12px;
            }
            .desktop-actions{ display:none !important; }
          }
          @media (min-width: 992px){
            .mobile-cartbar{ display:none !important; }
          }

          .pdp-help{ font-size:12px; color: rgba(0,0,0,.55); }
        `}
      </style>

      <section className="section">
        <Container>
          {isError ? (
            <Alert variant="danger" className="mb-0">
              {errorText}
            </Alert>
          ) : (
            <Row className="gx-3 gy-3">
              <Col lg={12}>
                <div className="pdp-card pdp-card-pad">
                  <Row className="gx-3 gy-3 align-items-start">
                    <Col lg={6}>
                      <div
                        ref={gallerySectionRef}
                        className="position-relative pdp-soft-bg pdp-main-img"
                      >
                        <div className="pdp-badge">Trending</div>

                        {sliderImg.length > 0 ? (
                          <button
                            type="button"
                            className="pdp-gallery-open"
                            onClick={() =>
                              openGalleryAt(
                                Math.max(0, sliderImg.findIndex((x) => x.id === selectedId)),
                                selectedId ? galleryItemRefs.current.get(selectedId) || null : null
                              )
                            }
                          >
                            <i className="ri-fullscreen-line me-1" />
                            View Gallery
                          </button>
                        ) : null}

                        <Swiper
                          rewind
                          navigation
                          modules={[Navigation]}
                          className="swiper productSwiper2 swiper-backface-hidden"
                          onSwiper={(sw) => (mainSwiperRef.current = sw)}
                          onSlideChange={(sw) => {
                            const item = sliderImg[sw.activeIndex];
                            if (item?.id) setSelectedId(item.id);
                          }}
                        >
                          {sliderImg.length ? (
                            sliderImg.map((item, idx) => (
                              <SwiperSlide key={item.id}>
                                <div
                                  ref={(node) => {
                                    if (node) {
                                      galleryItemRefs.current.set(item.id, node);
                                    } else {
                                      galleryItemRefs.current.delete(item.id);
                                    }
                                  }}
                                  className="bg-light w-100 pdp-main-slide-click"
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => openGalleryAt(idx, e.currentTarget)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      openGalleryAt(idx, e.currentTarget as HTMLDivElement);
                                    }
                                  }}
                                >
                                  <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "100%", aspectRatio: "3 / 4", padding: 8, overflow: "hidden" }}
                                  >
                                    <Image
                                      src={item.img}
                                      alt={item.alt || ""}
                                      fluid
                                      loading={idx === 0 || item.id === selectedId ? "eager" : "lazy"}
                                      decoding="async"
                                      onLoad={(e) => {
                                        storeImageDimensions(
                                          item.id,
                                          e.currentTarget.naturalWidth,
                                          e.currentTarget.naturalHeight
                                        );
                                      }}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        objectPosition: "center",
                                      }}
                                    />
                                  </div>
                                </div>
                              </SwiperSlide>
                            ))
                          ) : (
                            <SwiperSlide key="empty">
                              <div className="p-5 text-center text-muted">No images</div>
                            </SwiperSlide>
                          )}
                        </Swiper>
                      </div>

                      <div className="pdp-thumbs">
                        <Swiper
                          modules={[FreeMode]}
                          freeMode
                          watchSlidesProgress
                          slideToClickedSlide
                          direction="horizontal"
                          spaceBetween={10}
                          slidesPerView={5}
                          className="swiper productSwiper swiper-thumbs"
                        >
                          {sliderImg.map((item: SliderItem, idx: number) => (
                            <SwiperSlide key={item.id}>
                              <div
                                onClick={() => {
                                  mainSwiperRef.current?.slideTo(idx);
                                  setSelectedId(item.id);
                                }}
                                className={`pdp-thumb ${item.id === selectedId ? "is-active" : ""}`}
                                style={{ width: "100%", aspectRatio: "1 / 1" }}
                              >
                                <Image
                                  src={item.thumb}
                                  alt={item.alt || ""}
                                  fluid
                                  loading="lazy"
                                  decoding="async"
                                />
                              </div>
                            </SwiperSlide>
                          ))}
                        </Swiper>
                      </div>

                      {showCustomization ? (
                        <div className="mt-2 pdp-help">
                          Tip: Use the <b>Color Chart</b> image in gallery and type the color code/name below.
                        </div>
                      ) : null}
                    </Col>

                    <Col lg={6}>
                      <div className="pdp-details">
                        <div className="mb-3">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <div className="fs-15 text-warning">
                              <i className="ri-star-fill align-bottom" />
                              <i className="ri-star-fill align-bottom" />
                              <i className="ri-star-fill align-bottom" />
                              <i className="ri-star-fill align-bottom" />
                              <i className="ri-star-half-fill align-bottom" />
                            </div>
                            <span className="fw-medium">({product?.reviews_count ?? 0} Review)</span>
                            {isFetching ? <span className="text-muted ms-2">(updating)</span> : null}
                          </div>

                          <h4 className="lh-base mb-1 pdp-title">{product?.name || "-"}</h4>

                          <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                            <span className="badge bg-light text-dark border">
                              Product ID: #{product?.id ?? "-"}
                            </span>
                          </div>

                          <h5 className="mb-3 pdp-price">
                            {pricing.currency} {pricing.showPrice}{" "}
                            <span className="text-muted fs-14">
                              {pricing.compareAt != null ? (
                                <del>
                                  {pricing.currency} {pricing.compareAt.toFixed(2)}
                                </del>
                              ) : null}
                            </span>
                            {pricing.discountPct != null ? (
                              <span className="fs-14 ms-2 text-danger">({pricing.discountPct}% off)</span>
                            ) : null}
                          </h5>

                          <h6 className="fs-14 text-muted mb-2">Dispatch & Delivery :</h6>
                          <ul className="list-unstyled vstack gap-2 mb-0">
                            <li>
                              <div className="d-flex gap-3">
                                <div className="flex-shrink-0">
                                  <i className="ri-time-line text-primary align-middle fs-15" />
                                </div>
                                <div className="flex-grow-1">
                                  <b>Dispatch Timeline</b> {dispatchNote}
                                </div>
                              </div>
                            </li>
                            <li>
                              <div className="d-flex gap-3">
                                <div className="flex-shrink-0">
                                  <i className="ri-truck-line text-primary align-middle fs-15" />
                                </div>
                                <div className="flex-grow-1">
                                  <b>Transit Timeline</b> {transitNote}
                                </div>
                              </div>
                            </li>
                          </ul>
                        </div>

                        <hr className="my-3" />

                        <div className="d-flex align-items-center mb-3">
                          <h5 className="fs-15 mb-0">Quantity:</h5>
                          <div className="input-step ms-2">
                            <Button className="minus" onClick={() => setCount((v) => Math.max(1, v - 1))}>
                              –
                            </Button>
                            <Form.Control
                              type="number"
                              className="product-quantity1"
                              value={safeCount}
                              min={1}
                              max={100}
                              readOnly
                            />
                            <Button className="plus" onClick={() => setCount((v) => Math.min(100, v + 1))}>
                              +
                            </Button>
                          </div>
                        </div>

                        <Row className="gy-3">
                          {styleValues.length > 0 ? (
                            <Col xs={12}>
                              <div>
                                <h6 className="fs-14 fw-medium text-muted mb-2">Style:</h6>
                                <div className="d-flex flex-wrap gap-2">
                                  {styleValues.map((st: string, idx: number) => (
                                    <Button
                                      key={`style-${idx}`}
                                      size="sm"
                                      variant={selectedStyle === st ? "primary" : "outline-primary"}
                                      onClick={() => setSelectedStyle(st)}
                                    >
                                      {styleLabelMap.get(st) || formatFallbackAttributeLabel(st)}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                            </Col>
                          ) : null}

                          {sizeValues.length > 0 ? (
                            <Col xs={12}>
                              <div>
                                <h6 className="fs-14 fw-medium text-muted mb-2">Sizes:</h6>
                                <ul className="clothe-size list-unstyled hstack gap-2 mb-0 flex-wrap">
                                  {sizeValues.map((sz: string, idx: number) => {
                                    const id = `product-size-${idx}`;
                                    const rawValue = String(sz);
                                    const displayLabel =
                                      sizeLabelMap.get(rawValue) || formatFallbackAttributeLabel(rawValue);
                                    return (
                                      <li key={id}>
                                        <Form.Control
                                          type="radio"
                                          name="sizes7"
                                          id={id}
                                          checked={selectedSize === rawValue}
                                          onChange={() => setSelectedSize(rawValue)}
                                        />
                                        <Form.Label
                                          className="avatar-xs btn btn-soft-primary p-0 fs-12 d-flex align-items-center justify-content-center rounded-circle"
                                          htmlFor={id}
                                          title={displayLabel}
                                        >
                                          {displayLabel}
                                        </Form.Label>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            </Col>
                          ) : null}

                          {colorValues.length > 0 ? (
                            <Col xs={12}>
                              <div>
                                <h6 className="fs-14 fw-medium text-muted mb-2">Colors:</h6>
                                <ul className="clothe-colors list-unstyled hstack gap-1 mb-0 flex-wrap">
                                  {colorValues.map((c: string, idx: number) => {
                                    const id = `product-color-${idx}`;
                                    const rawValue = String(c);
                                    const displayLabel =
                                      colorLabelMap.get(rawValue) || formatFallbackAttributeLabel(rawValue);

                                    const hex = colorHexMap.get(rawValue) || null;

                                    const classes = [
                                      "btn btn-info",
                                      "btn btn-light",
                                      "btn btn-primary",
                                      "btn btn-success",
                                      "btn btn-warning",
                                      "btn btn-danger",
                                    ];
                                    const cls = classes[idx % classes.length];
                                    const isActive = selectedColor === rawValue;

                                    return (
                                      <li key={id} title={displayLabel}>
                                        <Form.Control
                                          type="radio"
                                          name="colors"
                                          id={id}
                                          checked={isActive}
                                          onChange={() => {
                                            colorSelectionPendingRef.current = true;
                                            setSelectedColor(rawValue);
                                          }}
                                        />
                                        <Form.Label
                                          className={`avatar-xs ${hex ? "" : cls} p-0 d-flex align-items-center justify-content-center rounded-circle pdp-color-option ${
                                            isActive ? "is-active" : ""
                                          }`}
                                          htmlFor={id}
                                          aria-label={displayLabel}
                                          style={
                                            hex
                                              ? {
                                                  backgroundColor: hex,
                                                  border: "1px solid rgba(0,0,0,.12)",
                                                  boxShadow: "0 6px 14px rgba(0,0,0,.06)",
                                                  cursor: "pointer",
                                                }
                                              : { cursor: "pointer" }
                                          }
                                        />
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            </Col>
                          ) : null}

                          {showCustomization ? (
                            <Col xs={12}>
                              <div className="mt-2">
                                <div className="d-flex align-items-center justify-content-between">
                                  <div>
                                    <h6 className="fs-14 fw-medium mb-1">Customization</h6>
                                    <div className="pdp-help">
                                      Enable if you want to request a custom color + add note (use the color chart image).
                                    </div>
                                  </div>

                                  <Form.Check
                                    type="switch"
                                    id="customization-switch"
                                    label={customizationEnabled ? "Enabled" : "Off"}
                                    checked={customizationEnabled}
                                    onChange={(e) => setCustomizationEnabled(e.target.checked)}
                                  />
                                </div>

                                {customizationEnabled ? (
                                  <div className="mt-3">
                                    <Row className="g-2">
                                      <Col xs={12}>
                                        <Form.Label className="mb-1 fw-medium">Required Colour / Code</Form.Label>
                                        <Form.Control
                                          value={customReqColor}
                                          onChange={(e) => setCustomReqColor(e.target.value)}
                                          placeholder="Example: Peach / #DF6565 / 'Refer chart: 12'"
                                        />
                                        <div className="pdp-help mt-1">
                                          Tip: open the <b>color chart</b> image in gallery and type the exact code/name.
                                        </div>
                                      </Col>

                                      <Col xs={12}>
                                        <Form.Label className="mb-1 fw-medium">Note (Stitching / Special request)</Form.Label>
                                        <Form.Control
                                          as="textarea"
                                          rows={4}
                                          value={customNote}
                                          onChange={(e) => setCustomNote(e.target.value)}
                                          placeholder="Write your customization note here..."
                                          style={{ resize: "vertical", minHeight: 120 }}
                                        />
                                      </Col>
                                    </Row>
                                  </div>
                                ) : null}
                              </div>
                            </Col>
                          ) : null}
                        </Row>

                        <div className="mt-4 desktop-actions">
                          {addErrorText ? (
                            <Alert variant="danger" className="py-2 mb-2">
                              {addErrorText}
                            </Alert>
                          ) : null}

                          {shareFeedback ? (
                            <Alert variant={shareFeedback.variant} className="py-2 mb-2">
                              {shareFeedback.message}
                            </Alert>
                          ) : null}

                          <div className="hstack gap-2">
                            <Button
                              variant="success"
                              className="btn btn-hover w-100"
                              onClick={handleAddToCart}
                              disabled={!canBuy || isAdding}
                            >
                              {isAdding ? (
                                <Spinner size="sm" animation="border" className="me-2" />
                              ) : (
                                <i className="bi bi-basket2 me-2" />
                              )}
                              Add To Cart
                            </Button>

                            <Button
                              variant="primary"
                              className="btn btn-hover w-100"
                              onClick={handleBuyNow}
                              disabled={!canBuy || isAdding}
                            >
                              <i className="bi bi-cart2 me-2" /> Buy Now
                            </Button>

                            <Button
                              className={`btn btn-hover ${wishlisted ? "btn-danger" : "btn-soft-danger"}`}
                              aria-pressed={wishlisted}
                              onClick={() => setWishlisted((v) => !v)}
                              title={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
                            >
                              {wishlisted ? (
                                <span>
                                  <i className="ri-heart-fill" />
                                </span>
                              ) : (
                                <span>
                                  <i className="ri-heart-line" />
                                </span>
                              )}
                            </Button>

                            <Button
                              className="btn btn-hover btn-soft-dark"
                              onClick={handleShareProduct}
                              title="Share product"
                              aria-label="Share product"
                            >
                              <span>
                                <i className="ri-share-forward-line" />
                              </span>
                            </Button>
                          </div>
                        </div>

                        <div className="mobile-cartbar-spacer" />
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          )}
        </Container>
      </section>

      {!isLoading && !isError && product ? (
        <div className="mobile-cartbar">
          {addErrorText ? (
            <div className="mb-2">
              <small className="text-danger">{addErrorText}</small>
            </div>
          ) : null}

          {shareFeedback ? (
            <div className="mb-2">
              <small className={shareFeedback.variant === "success" ? "text-success" : "text-danger"}>
                {shareFeedback.message}
              </small>
            </div>
          ) : null}

          <div className="d-flex align-items-center gap-2">
            <Button variant="success" className="w-100" onClick={handleAddToCart} disabled={!canBuy || isAdding}>
              {isAdding ? <Spinner size="sm" animation="border" className="me-2" /> : <i className="bi bi-basket2 me-2" />}
              Add To Cart
            </Button>

            <Button variant="primary" className="w-100" onClick={handleBuyNow} disabled={!canBuy || isAdding}>
              <i className="bi bi-cart2 me-2" /> Buy
            </Button>

            <Button
              className={`btn ${wishlisted ? "btn-danger" : "btn-soft-danger"}`}
              onClick={() => setWishlisted((v) => !v)}
              title={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
              style={{ width: 46, height: 46 }}
            >
              <i className={wishlisted ? "ri-heart-fill" : "ri-heart-line"} />
            </Button>

            <Button
              className="btn btn-soft-dark"
              onClick={handleShareProduct}
              title="Share product"
              aria-label="Share product"
              style={{ width: 46, height: 46 }}
            >
              <i className="ri-share-forward-line" />
            </Button>
          </div>
        </div>
      ) : null}

      <section className="section pt-0">
        <Container>
          <div className="pdp-card pdp-card-pad">
            <Tab.Container
              id="left-tabs-example"
              activeKey={activeDetailTab}
              onSelect={(eventKey) => setActiveDetailTab(eventKey || "Description")}
            >
              <Row>
                <Col sm={12}>
                  <Nav variant="tabs" className="nav-tabs-custom mb-3">
                    <Nav.Item as="li">
                      <Nav.Link as="a" eventKey="Description">
                        Description
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item as="li">
                      <Nav.Link as="a" eventKey="FAQ">
                        FAQ
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item as="li">
                      <Nav.Link as="a" eventKey="Ratings">
                        Ratings Reviews
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>

                  <Tab.Content>
                    <Tab.Pane eventKey="Description">
                      <div className="text-muted fs-15 product-html">
                        {htmlFullDesc?.trim() ? (
                          <div dangerouslySetInnerHTML={{ __html: htmlFullDesc }} />
                        ) : (
                          <div className="text-muted">No description available.</div>
                        )}
                      </div>
                    </Tab.Pane>

                    <Tab.Pane eventKey="FAQ">
                      {isFaqLoading || isFaqFetching ? (
                        <div className="py-4 text-muted d-flex align-items-center gap-2">
                          <Spinner size="sm" animation="border" />
                          Loading frequently asked questions...
                        </div>
                      ) : faqItems.length > 0 ? (
                        <Accordion defaultActiveKey="0" className="accordion-border-box">
                          {faqItems.map((item, idx) => (
                            <Accordion.Item eventKey={String(idx)} key={`pdp-faq-${item.id}`}>
                              <Accordion.Header>{item.question}</Accordion.Header>
                              <Accordion.Body>
                                <div
                                  className="text-muted fs-15 product-html"
                                  dangerouslySetInnerHTML={{ __html: item.answer || "" }}
                                />
                              </Accordion.Body>
                            </Accordion.Item>
                          ))}
                        </Accordion>
                      ) : (
                        <div className="text-muted py-4">No FAQs available right now.</div>
                      )}
                    </Tab.Pane>

                    <Tab.Pane eventKey="Ratings">
                      <div>
                        <div className="d-flex flex-wrap gap-4 justify-content-between align-items-center mt-4">
                          <div className="flex-shrink-0">
                            <h5 className="fs-15 mb-3 fw-medium">Total Rating's</h5>
                            <h2 className="fw-bold mb-3">{product?.reviews_count ?? 0}</h2>
                            <p className="text-muted mb-0">Growth in reviews on this year</p>
                          </div>
                          <hr className="vr" />
                          <div className="flex-shrink-0">
                            <h5 className="fs-15 mb-3 fw-medium">Average Rating</h5>
                            <h2 className="fw-bold mb-3">
                              {Number(product?.reviews_avg ?? 0).toFixed(1)}
                              <span className="fs-16 align-middle text-warning ms-2">
                                <i className="ri-star-fill" />
                                <i className="ri-star-fill" />
                                <i className="ri-star-fill" />
                                <i className="ri-star-fill" />
                                <i className="ri-star-half-fill" />
                              </span>
                            </h2>
                            <p className="text-muted mb-0">Average rating on this year</p>
                          </div>
                          <hr className="vr" />
                          <div className="flex-shrink-0 w-xl">
                            {(productprogress || []).map((item: any, idx: number) => (
                              <Row className="align-items-center g-3 align-items-center mb-2" key={idx}>
                                <Col className="col-auto">
                                  <h6 className="mb-0 text-muted fs-13">
                                    <i className="ri-star-fill text-warning me-1 align-bottom" />
                                    {item.num}
                                  </h6>
                                </Col>
                                <Col>
                                  <ProgressBar now={item.value} variant={`${item.color}`} className="progress animated-progress progress-sm" />
                                </Col>
                                <Col className="col-auto">
                                  <h6 className="mb-0 text-muted fs-13">{item.progressnum}</h6>
                                </Col>
                              </Row>
                            ))}
                          </div>
                        </div>

                        <SimpleBar className="mt-4" style={{ maxHeight: "350px" }}>
                          <div className="d-flex p-3 border-bottom border-bottom-dashed">
                            <div className="flex-shrink-0 me-3">
                              <Image className="avatar-xs" src={avatar5} alt="" roundedCircle />
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex mb-3">
                                <div className="flex-grow-1">
                                  <div className="mb-2 fs-12">
                                    <span><i className="ri-star-fill text-warning align-bottom" /></span>
                                    <span><i className="ri-star-fill text-warning align-bottom" /></span>
                                    <span><i className="ri-star-fill text-warning align-bottom" /></span>
                                    <span><i className="ri-star-line text-warning align-bottom" /></span>
                                    <span><i className="ri-star-line text-warning align-bottom" /></span>
                                  </div>
                                  <h6 className="mb-0">Donald Risher</h6>
                                </div>
                                <div className="flex-shrink-0">
                                  <p className="mb-0 text-muted">
                                    <i className="ri-calendar-event-fill me-2 align-middle" />
                                    Aug 16, 2022
                                  </p>
                                </div>
                              </div>
                              <div>
                                <h5 className="lh-base fs-15">Design Quality</h5>
                                <p className="mb-0">
                                  " This is an incredible framework worth so much in the right hands! Nowhere else are you going to get so much flexibility and great code for a few dollars. Highly recommend purchasing today! "
                                </p>
                              </div>
                            </div>
                          </div>
                        </SimpleBar>
                      </div>
                    </Tab.Pane>
                  </Tab.Content>
                </Col>
              </Row>
            </Tab.Container>
          </div>
        </Container>
      </section>

      {product && (similarProducts.length > 0 || isSimilarFetching) ? (
        <section className="section pt-0">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <h4 className="mb-0">Similar Products</h4>
                  {isSimilarFetching ? <small className="text-muted">Loading...</small> : null}
                </div>
              </Col>
            </Row>

            <Row className="gy-4">
              {similarProducts.map((item) => (
                <Col xl={3} lg={4} md={6} sm={6} key={item.id}>
                  <Card className="pdp-similar-card h-100 border-0 shadow-none">
                    <div className="pdp-similar-image-wrap">
                      <Link to={`/product/${item.slug}`} className="d-block">
                        <div className="pdp-similar-image-box">
                          {item.img ? (
                            <Image src={item.img} alt={item.alt || item.title} fluid className="pdp-similar-image" />
                          ) : (
                            <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted">
                              No image
                            </div>
                          )}
                        </div>
                      </Link>
                    </div>

                    <Card.Body className="d-flex flex-column">
                      <Link to={`/product/${item.slug}`} className="pdp-similar-title">
                        {item.title}
                      </Link>

                      <div className="mt-3 d-flex align-items-center gap-2 flex-wrap">
                        <span className="pdp-similar-price">{item.price}</span>
                        {item.compareAt ? (
                          <span className="pdp-similar-old-price">{item.compareAt}</span>
                        ) : null}
                      </div>

                      <div className="mt-3">
                        <Link to={`/product/${item.slug}`} className="btn btn-dark rounded-pill px-3">
                          Shop Now
                        </Link>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      ) : null}

      <CommonService />

    </React.Fragment>
  );
};

export default Productdetails;
